import { ErrorDocument } from '@/types';

/**
 * The expanded half of an error row.
 *
 * Split out of ErrorItem purely for size: the row and its detail panel are two
 * unrelated jobs, and keeping them in one file pushed it past the point where
 * either could be read comfortably.
 */
export function ErrorDetails({ error }: { error: ErrorDocument }) {
  const firstSeen = error.timestamp?.toDate();
  const lastSeen = error.lastSeen?.toDate();
  const resolvedAt = error.resolvedAt?.toDate();
  const investigatingAt = error.investigatingAt?.toDate();
  const hasMetadata =
    !!error.metadata &&
    Object.keys(error.metadata).some((key) => error.metadata[key as keyof typeof error.metadata]);

  return (
    <div className="space-y-4 border-t border-slate-100 bg-slate-50 px-4 py-4 sm:px-5">
      {error.errorCode && <DetailRow label="Error Code" value={error.errorCode} />}
      <DetailRow label="Error Hash" value={error.hash} mono />

      <Section title="Timeline">
        {firstSeen && <DetailRow label="First seen" value={firstSeen.toLocaleString()} />}
        {lastSeen && <DetailRow label="Last seen" value={lastSeen.toLocaleString()} />}
        <DetailRow label="Occurrences" value={String(error.count)} />
        {investigatingAt && (
          <DetailRow
            label="Investigating since"
            value={`${investigatingAt.toLocaleString()}${
              error.investigatingBy ? ` by ${error.investigatingBy}` : ''
            }`}
          />
        )}
        {resolvedAt && (
          <DetailRow
            label="Resolved at"
            value={`${resolvedAt.toLocaleString()}${
              error.resolvedBy ? ` by ${error.resolvedBy}` : ''
            }`}
          />
        )}
      </Section>

      {hasMetadata && (
        <Section title="Metadata">
          {error.metadata.environment && (
            <DetailRow label="Environment" value={error.metadata.environment} />
          )}
          {error.metadata.requestId && (
            <DetailRow label="Request ID" value={error.metadata.requestId} mono />
          )}
          {error.metadata.userId && (
            <DetailRow label="User ID" value={error.metadata.userId} mono />
          )}
          {error.metadata.url && <DetailRow label="URL" value={error.metadata.url} mono />}
          {error.metadata.module && <DetailRow label="Module" value={error.metadata.module} />}
          {error.metadata.component && (
            <DetailRow label="Component" value={error.metadata.component} />
          )}
          {error.metadata.category && (
            <DetailRow label="Category" value={error.metadata.category} />
          )}
          {error.metadata.userAgent && (
            <DetailRow label="User Agent" value={error.metadata.userAgent} truncate />
          )}
        </Section>
      )}

      {error.stack && (
        <Section title="Stack Trace">
          <pre className="max-h-64 overflow-x-auto overflow-y-auto whitespace-pre-wrap break-words rounded-lg bg-slate-900 p-3 text-xs text-slate-100">
            {error.stack}
          </pre>
        </Section>
      )}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {title}
      </h4>
      <div className="space-y-1">{children}</div>
    </div>
  );
}

function DetailRow({
  label,
  value,
  mono = false,
  truncate = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
  truncate?: boolean;
}) {
  return (
    <div className="flex justify-between gap-4 text-xs">
      <span className="shrink-0 text-slate-500">{label}</span>
      <span
        className={`text-right text-slate-900 ${mono ? 'font-mono' : ''} ${
          truncate ? 'truncate' : 'break-all'
        }`}
        title={value}
      >
        {value}
      </span>
    </div>
  );
}
